import { Main, Models, DataTable, Pagination, SearchInput, StatusBadge } from 'utils/imports.utils';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetState, buildAdminListQuery, formatViewCount, getAdminUserLabel } from 'utils/functions.utils';
import { AdminProjectRow } from 'interfaces/admin.interface';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import FilterChips from 'common_components/ui/filter_chips/filter_chips.ui';
import './admin_projects.screen.scss';

const PAGE_SIZE = 20;

const STATUS_CHIPS = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];


const AdminProjects = () => {
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    projects: [] as AdminProjectRow[],
    totalDocs: 0,
    page: 1,
    search: '',
    statusFilter: '',
    loading: true,
  });

  const fetchProjects = async (page: number, search: string, statusFilter: string) => {
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const query = buildAdminListQuery({ skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, search, project_status: statusFilter });
      const response: any = await Models.auth.adminListProjects(query);
      setState({
        projects: response?.data?.docs || [],
        totalDocs: response?.data?.totalDocs || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch projects', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchProjects(state.page, state.search, state.statusFilter);
    } else {
      navigate('/login');
    }
  }, [state.page, state.search, state.statusFilter]);

  const columns: IDataTableColumn[] = [
    {
      key: 'project_name',
      label: 'Project',
      width: '30%',
      render: (row: AdminProjectRow) => <span className="admin_projects_name">{row.project_name || '—'}</span>,
    },
    {
      key: 'owner',
      label: 'Owner',
      width: '24%',
      render: (row: AdminProjectRow) => <span className="admin_projects_owner">{(row.owner && getAdminUserLabel(row.owner)) || '—'}</span>,
    },
    {
      key: 'project_status',
      label: 'Status',
      width: '16%',
      render: (row: AdminProjectRow) =>
        row.project_status ? (
          <StatusBadge status={row.project_status} />
        ) : (
          '—'
        ),
    },
    {
      key: 'total_views',
      label: 'Views',
      width: '10%',
      align: 'right',
      render: (row: AdminProjectRow) => <span className="admin_projects_views">{formatViewCount(row.total_views)}</span>,
    },
    { key: 'target_count', label: 'Targets', width: '10%', align: 'right' },
    {
      key: 'created_at',
      label: 'Created',
      width: '10%',
      align: 'right',
      render: (row: AdminProjectRow) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : '—',
    },
  ];

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Projects" title="Projects">
        <div className="admin_projects">
          <div className="admin_projects_toolbar">
            <div className="admin_projects_search">
              <SearchInput
                onSearch={(value) => setState({ search: value, page: 1 })}
                placeholder="Search projects"
              />
            </div>
            <FilterChips
              options={STATUS_CHIPS}
              value={state.statusFilter}
              onChange={(value) => setState({ statusFilter: value, page: 1 })}
            />
          </div>
          <div className="admin_projects_card">
            <DataTable
              columns={columns}
              rows={state.projects}
              rowKey="_id"
              loading={state.loading}
              emptyMessage="No projects found"
              onRowClick={(row: AdminProjectRow) => navigate(`/admin/projects/${row._id}`)}
            />
          </div>
          <Pagination
            currentPage={state.page}
            totalPages={Math.ceil(state.totalDocs / PAGE_SIZE)}
            pageSize={PAGE_SIZE}
            totalItems={state.totalDocs}
            onPageChange={(page) => setState({ page })}
          />
        </div>
      </AdminPage>
    </Main>
  );
};

export default AdminProjects;
